import React from 'react'
import { useTheme } from '@mui/system'
import HorizontalBarChart from './utils/HorizontalBarChart'
import { BarSeriesType } from '@mui/x-charts'
import { calculateIpoOutcome } from '../../pages/issues/utils'
import { EnrichedIssueEntity, IssueEntity } from 'types/IssueEntity'
import { roundFloat } from '../../packages/utils/numberFormatters'

/**
 * If value is a null, NaN or undefined, or empty string return zero. If it is a string, convert to float.
 */
const v = (value: number | null | string) => {
    if (value === null || value === undefined || value === '' || isNaN(+value)) {
        return 0
    }
    if (typeof value === 'string') {
        return parseFloat(value)
    }
    return value
}

function IpoOutcomeChart({
    isLeftAlign,
    issue,
    skipAnimation,
}: {
    isLeftAlign?: boolean
    issue: IssueEntity
    skipAnimation?: boolean
}) {
    const theme = useTheme()

    if (issue.unitSubTotal === null && issue.secondarySub === null) return null

    const outcome = calculateIpoOutcome(issue)

    const series: Omit<BarSeriesType, 'type'>[] = []
    const totalSub = outcome.pctSubCommitment + outcome.pctSubscribedPublic + outcome.pctSubscribedGuarantors

    if (outcome.pctSubCommitment > 0) {
        series.push({
            label: 'Teckningsåtagare',
            data: [outcome.pctSubCommitment],
            color: theme.palette.success.main,
        })
    }
    if (outcome.pctSubscribedPublic > 0) {
        series.push({
            label: 'Allmänheten',
            data: [outcome.pctSubscribedPublic],
            color: theme.palette.success.light,
        })
    }
    if (outcome.pctSubscribedGuarantors > 0) {
        series.push({
            label: 'Garanter',
            data: [outcome.pctSubscribedGuarantors],
            color: theme.palette.warning.main,
        })
    }
    if (outcome.pctNotSubscribed > 0) {
        series.push({
            label: 'Ej tecknat',
            data: [outcome.pctNotSubscribed],
            color: theme.palette.grey[200],
        })
    }

    return (
        <HorizontalBarChart
            isLeftAlign={isLeftAlign}
            labelValue={totalSub || 0}
            series={series}
            skipAnimation={skipAnimation}
        />
    )
}
function DeafultOutcomeChart({
    isLeftAlign,
    issue,
    skipAnimation,
}: {
    isLeftAlign?: boolean
    issue: EnrichedIssueEntity
    skipAnimation?: boolean
}) {
    const {
        unitSubWithRights,
        unitSubWithoutRights,
        unitPctSubWithRights,
        unitPctSubWithoutRights,
        unitSubGuarantor,
        unitPctSubGuarantor,
        totalOfferedUnits,
        unitSubTotal,
    } = issue

    const theme = useTheme()

    if (!unitSubTotal) return null

    let series: Omit<BarSeriesType, 'type'>[] | null = null

    const withAndWithoutRightsAbsolute =
        v(totalOfferedUnits) > 0 ? 100.0 * ((v(unitSubWithRights) + v(unitSubWithoutRights)) / v(totalOfferedUnits)) : 0
    let withAndWithoutRights = withAndWithoutRightsAbsolute + v(unitPctSubWithRights) + v(unitPctSubWithoutRights)
    const guarantorsAbsolute = v(totalOfferedUnits) > 0 ? 100.0 * (v(unitSubGuarantor) / v(totalOfferedUnits)) : 0
    let guarantors = guarantorsAbsolute + v(unitPctSubGuarantor)
    let noSub = v(totalOfferedUnits) > 0 ? 100.0 * ((v(totalOfferedUnits) - v(unitSubTotal)) / v(totalOfferedUnits)) : 0
    const totalSub = v(totalOfferedUnits) > 0 ? (100.0 * v(unitSubTotal)) / v(totalOfferedUnits) : 0

    const calculatedTotal = withAndWithoutRights + guarantors + noSub
    if (calculatedTotal > 100) {
        // Fix the percentages if they are over 100, to avoid a collapsed chart
        withAndWithoutRights = (withAndWithoutRights / calculatedTotal) * 100
        guarantors = (guarantors / calculatedTotal) * 100
        noSub = (noSub / calculatedTotal) * 100
    }

    if (issue.noGuarantors === 1) {
        // No guarantors in this issue
        series = [
            {
                label: 'Med och utan rätter',
                data: [roundFloat(withAndWithoutRights, 4)],
                color: theme.palette.success.main,
            },
            {
                label: 'Ej tecknat',
                data: [roundFloat(noSub, 4)],
                color: theme.palette.grey[200],
            },
        ]
    } else {
        series = [
            {
                label: 'Med och utan rätter',
                data: [roundFloat(withAndWithoutRights, 4)],
                color: theme.palette.success.main,
            },
            {
                label: 'Garanter',
                data: [roundFloat(guarantors, 4)],
                color: theme.palette.warning.main,
            },
            {
                label: 'Ej tecknat',
                data: [roundFloat(noSub, 4)],
                color: theme.palette.grey[200],
            },
        ]
    }

    return (
        <HorizontalBarChart
            isLeftAlign={isLeftAlign}
            labelValue={totalSub || 0}
            series={series}
            skipAnimation={skipAnimation}
        />
    )
}

function OutcomeChart({
    isLeftAlign,
    issue,
    skipAnimation,
}: {
    isLeftAlign?: boolean
    issue: IssueEntity
    skipAnimation?: boolean
}) {
    if (issue.type === 'IPO') {
        return <IpoOutcomeChart isLeftAlign={isLeftAlign} issue={issue} skipAnimation={skipAnimation} />
    }

    return <DeafultOutcomeChart isLeftAlign={isLeftAlign} issue={issue} skipAnimation={skipAnimation} />
}

export default OutcomeChart
